import { useState, useRef } from 'react';
import { Upload, FileUp, Loader2, CheckCircle2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';

interface ExtractedData {
  vendor: string;
  invoice_number: string;
  amount: number;
  currency: string;
  due_date: string;
}

interface InvoiceUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUploaded: () => void;
}

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
const ACCEPTED_TYPES = ['application/pdf', 'text/xml', 'application/xml'];

type UploadStep = 'idle' | 'uploading' | 'parsing' | 'review' | 'error';

export function InvoiceUploadDialog({ open, onOpenChange, onUploaded }: InvoiceUploadDialogProps) {
  const [step, setStep] = useState<UploadStep>('idle');
  const [file, setFile] = useState<File | null>(null);
  const [extracted, setExtracted] = useState<ExtractedData | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setStep('idle');
    setFile(null);
    setExtracted(null);
    setErrorMsg('');
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) reset();
    onOpenChange(isOpen);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    if (!ACCEPTED_TYPES.includes(selected.type)) {
      setErrorMsg('Only PDF and XML files are accepted.');
      setStep('error');
      return;
    }
    if (selected.size > MAX_FILE_SIZE) {
      setErrorMsg('File size exceeds 20MB limit.');
      setStep('error');
      return;
    }
    setFile(selected);
    setErrorMsg('');
  };

  const handleUpload = async () => {
    if (!file) return;

    try {
      setStep('uploading');
      const filePath = `invoices/${crypto.randomUUID()}-${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('finance')
        .upload(filePath, file);
      if (uploadError) throw uploadError;

      setStep('parsing');
      const { data, error: parseError } = await supabase.functions.invoke('invoice-upload', {
        body: { file_path: filePath, file_name: file.name },
      });
      if (parseError) throw parseError;

      setExtracted(data as ExtractedData);
      setStep('review');
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Upload failed');
      setStep('error');
    }
  };

  const handleConfirm = () => {
    handleClose(false);
    onUploaded();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload Invoice</DialogTitle>
        </DialogHeader>

        {step === 'idle' && (
          <div className="space-y-4">
            <div
              onClick={() => fileInputRef.current?.click()}
              className="flex cursor-pointer flex-col items-center gap-3 rounded-lg border-2 border-dashed p-8 text-center transition-colors hover:border-primary hover:bg-muted/30"
            >
              <FileUp className="h-10 w-10 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Click to select a file</p>
                <p className="text-xs text-muted-foreground">PDF or XML, max 20MB</p>
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.xml"
              className="hidden"
              onChange={handleFileSelect}
            />
            {file && (
              <div className="flex items-center justify-between rounded-md bg-muted/50 px-3 py-2 text-sm">
                <span className="truncate">{file.name}</span>
                <span className="shrink-0 text-muted-foreground">
                  {(file.size / 1024).toFixed(0)} KB
                </span>
              </div>
            )}
            <Button onClick={handleUpload} disabled={!file} className="w-full">
              <Upload className="mr-2 h-4 w-4" /> Upload & Parse
            </Button>
          </div>
        )}

        {(step === 'uploading' || step === 'parsing') && (
          <div className="flex flex-col items-center gap-4 py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm font-medium">
              {step === 'uploading' ? 'Uploading file...' : 'Parsing invoice data...'}
            </p>
          </div>
        )}

        {step === 'review' && extracted && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-green-600">
              <CheckCircle2 className="h-4 w-4" />
              <span>Invoice parsed successfully</span>
            </div>
            <Card>
              <CardContent className="grid grid-cols-2 gap-3 pt-4 text-sm">
                <div>
                  <Label className="text-xs text-muted-foreground">Vendor</Label>
                  <p className="font-medium">{extracted.vendor}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Invoice #</Label>
                  <p className="font-medium">{extracted.invoice_number}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Amount</Label>
                  <p className="font-medium">
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: extracted.currency,
                    }).format(extracted.amount)}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Due Date</Label>
                  <p className="font-medium">
                    {new Date(extracted.due_date).toLocaleDateString()}
                  </p>
                </div>
              </CardContent>
            </Card>
            <Button onClick={handleConfirm} className="w-full">Confirm & Save</Button>
          </div>
        )}

        {step === 'error' && (
          <div className="space-y-4 py-4 text-center">
            <p className="text-sm text-destructive">{errorMsg}</p>
            <Button variant="outline" onClick={reset}>Try Again</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
